/**
 * The "a new version of britbank is available - reload" prompt (Issue #320).
 *
 * When a reconnect's `Welcome` reports a server build id different from this client's
 * baked one, {@link import('./clientUpdate').UpdateController} decides to prompt (the
 * default, non-force path) and calls {@link UpdatePromptHandle.show}. This module owns
 * only the DOM wiring - the markup + styling live in `index.html` - so it is headless-
 * testable under jsdom and reads no Pixi and no socket, mirroring
 * {@link import('./whatsNew').initWhatsNew}.
 *
 * It deliberately reuses the what's-new **card shape** but is styled as a **non-
 * blocking** corner banner (no full-screen backdrop): a player mid-duel / mid-storm
 * keeps full control of the game and reloads at a safe moment, or dismisses ("Later")
 * and keeps playing on the old bundle until they choose to refresh. The reload control
 * invokes the caller-supplied action (which performs the actual navigation).
 */

/** Options for {@link initUpdatePrompt}; the document is injected so tests can pass jsdom's. */
export interface UpdatePromptOptions {
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts` keeps to show the prompt when a newer server build is seen. */
export interface UpdatePromptHandle {
  /**
   * Show the prompt; its reload control calls `onReload`. Returns whether it opened
   * (a client whose markup is missing returns a harmless `false`). The prompt stays up
   * until the player reloads or dismisses it - the caller guards against re-showing it
   * on every reconnect.
   */
  show(onReload: () => void): boolean;
  /** Whether the prompt is currently visible. */
  isOpen(): boolean;
  /** Hide the prompt (as the "Later" button and a completed reload do). */
  close(): void;
}

/**
 * Wire the `#update-prompt` banner (declared hidden in `index.html`) into a handle.
 * The core parts (`#update-prompt`, `#update-prompt-reload`) must exist; if either is
 * missing the returned handle is a harmless no-op so a missing-markup client never
 * throws (mirrors {@link import('./whatsNew').initWhatsNew}). The "Later" dismiss
 * button (`#update-prompt-dismiss`) is optional.
 */
export function initUpdatePrompt(options: UpdatePromptOptions = {}): UpdatePromptHandle {
  const doc = options.doc ?? document;
  const overlay = doc.getElementById('update-prompt');
  const reloadBtn = doc.getElementById('update-prompt-reload');
  const dismissBtn = doc.getElementById('update-prompt-dismiss');
  if (!overlay || !reloadBtn) {
    return { show: () => false, isOpen: () => false, close: () => {} };
  }

  // The reload action for the currently-shown prompt; captured on `show` so the click
  // handler (wired once) always runs the latest caller's navigation.
  let onReload: (() => void) | null = null;

  const close = (): void => {
    overlay.hidden = true;
  };

  const show = (reload: () => void): boolean => {
    onReload = reload;
    overlay.hidden = false;
    // Focus the reload control so Enter triggers it and the banner is keyboard/screen-
    // reader reachable the instant it opens; focus can throw in some headless envs.
    try {
      (reloadBtn as HTMLButtonElement).focus();
    } catch {
      /* the banner is still usable without focus */
    }
    return true;
  };

  reloadBtn.addEventListener('click', () => {
    const reload = onReload;
    close();
    reload?.();
  });
  dismissBtn?.addEventListener('click', close);

  return { show, isOpen: () => !overlay.hidden, close };
}
